/**
 * `<SlotCard>` — wrapper that takes a `SlotDefinition` + `PresentContext`
 * (from the `interview:show-slot` IPC push) and dispatches to the right
 * per-type renderer.
 *
 * Layout mirrors `<BulkInterviewCard>`'s row treatment: title (slot.text),
 * context strip ("Question N of M, agent: <name>"), per-type body, action
 * buttons (Skip if !slot.required, Submit when filled).
 *
 * Submit shape: builds a `PresentResult` of kind `'answered'` with the
 * type-correct value (string / string[] / number / FreeTextWithVoice) and
 * hands it to the parent's `onSubmit`. Skip builds a `PresentResult` of
 * kind `'skipped'` with `reason: 'user-skipped'`.
 *
 * Pre-fill semantics match `<BulkInterviewCard>`:
 *   - The component seeds local state from `context.prefilled` (when
 *     supplied) so the user reads the engine's inference as the default.
 *   - The `prefilledUntouched` styling lifts on first interaction.
 */

import { useMemo, useState } from 'react';
import { Check } from 'lucide-react';
import type {
  PresentContext,
  PresentResult,
  SlotAnswerValue,
  SlotDefinition,
  FreeTextWithVoice,
} from '@volley/crucible/interview/types.js';
import { SlotCardSingleSelect } from './slot-cards/SlotCardSingleSelect';
import {
  SlotCardFreeText,
  reconstructFreeTextValue,
} from './slot-cards/SlotCardFreeText';
import {
  SlotCardMultiPick,
  isMultiPickValid,
} from './slot-cards/SlotCardMultiPick';
import {
  SlotCardDragRank,
  defaultRankFromOptions,
} from './slot-cards/SlotCardDragRank';
import {
  SlotCardSlider,
  defaultSliderValue,
} from './slot-cards/SlotCardSlider';
import { SlotCardImageGrid } from './slot-cards/SlotCardImageGrid';

export interface SlotCardProps {
  /** Game id this slot belongs to. Echoed back on the answer payload
   *  so the main-side gate can match the answer to the right hold-open
   *  Promise. */
  gameId: string;
  slot: SlotDefinition;
  context: PresentContext;
  /** Optional audio path read off `state.slotMeta[slotId].audioPath`
   *  for free-text slots with voice capability (per TDD §6.2). */
  audioPath?: string;
  /** Submit handler — receives the finished `PresentResult`. The parent
   *  forwards this to `interview:answer-slot`. */
  onSubmit: (result: PresentResult) => void;
  /** Skip handler — receives a pre-built `PresentResult` of kind
   *  `skipped`. Only available when `slot.required` is false. */
  onSkip: (result: PresentResult) => void;
  /** Pass true when the `newUi` feature flag is active to render the
   *  Slate-design card layout; defaults to false (legacy indigo layout). */
  newUi?: boolean;
}

// ---------------------------------------------------------------------------
// Pure helpers (extracted for tests)
// ---------------------------------------------------------------------------

/** Initial in-memory value for a slot, derived from `context.prefilled`
 *  when present; otherwise type-specific defaults. The shape is the
 *  CARD'S local state (a tagged union depending on slot.type), not the
 *  engine's `SlotAnswerValue`. We translate at submit time. */
export function initialValueForSlot(
  slot: SlotDefinition,
  prefilled: SlotAnswerValue | undefined,
): SlotCardLocalValue {
  switch (slot.type) {
    case 'single-select': {
      const v = typeof prefilled === 'string' ? prefilled : null;
      return { kind: 'single-select', value: v };
    }
    case 'free-text': {
      // Prefill may be a plain string OR a FreeTextWithVoice — we
      // peel either back to the transcript string. The audio path
      // arrives via the separate `audioPath` prop (read off slotMeta).
      let transcript = '';
      if (typeof prefilled === 'string') transcript = prefilled;
      else if (
        prefilled !== undefined &&
        !Array.isArray(prefilled) &&
        typeof prefilled === 'object' &&
        'kind' in prefilled &&
        prefilled.kind === 'voice'
      ) {
        transcript = prefilled.transcript;
      }
      return { kind: 'free-text', value: transcript };
    }
    case 'multi-pick': {
      const v = Array.isArray(prefilled) ? [...prefilled] : [];
      return { kind: 'multi-pick', value: v };
    }
    case 'drag-rank': {
      const v = Array.isArray(prefilled) && prefilled.length > 0
        ? [...prefilled]
        : defaultRankFromOptions(slot);
      return { kind: 'drag-rank', value: v };
    }
    case 'slider': {
      const v = typeof prefilled === 'number' ? prefilled : defaultSliderValue(slot);
      return { kind: 'slider', value: v };
    }
    case 'image-grid': {
      const v = Array.isArray(prefilled) ? [...prefilled] : [];
      return { kind: 'image-grid', value: v };
    }
  }
}

/**
 * Pure helper — true when the slot's configuration makes it impossible
 * to answer at all (e.g. the template ships `image-grid` with zero
 * images, or `single-select` with zero options). Used by the SlotCard
 * to override the required-hides-Skip rule so a required-but-empty
 * slot can be force-skipped instead of dead-ending the user.
 *
 * Codex Round-1 fix on PR #302. Only narrows on shapes that genuinely
 * have NO answer space — `free-text` and `slider` are always
 * answerable (the user can type anything / move the slider), so they
 * never fall into this branch.
 */
export function isSlotUnanswerable(slot: SlotDefinition): boolean {
  switch (slot.type) {
    case 'image-grid':
      // Codex Round-2 fix on PR #302: optional chain on `.images`. A
      // malformed template could ship `slot.imageGrid` set but
      // `images` undefined (the schema validator can't catch every
      // hand-edit); without `?.length` this would throw a TypeError
      // at runtime. Treat undefined-or-empty identically — neither is
      // answerable.
      return !slot.imageGrid || (slot.imageGrid.images?.length ?? 0) === 0;
    case 'single-select':
    case 'multi-pick':
    case 'drag-rank':
      return !slot.options || slot.options.length === 0;
    case 'free-text':
    case 'slider':
      return false;
  }
}

/** True when the local value is well-formed enough to submit. Mirrors
 *  the per-type validity logic; the parent's Submit gate calls this. */
export function isSlotAnswerSubmittable(
  slot: SlotDefinition,
  local: SlotCardLocalValue,
): boolean {
  switch (slot.type) {
    case 'single-select':
      return local.kind === 'single-select' && typeof local.value === 'string';
    case 'free-text':
      return local.kind === 'free-text' && local.value.trim().length > 0;
    case 'multi-pick':
      return local.kind === 'multi-pick' && isMultiPickValid(slot, local.value);
    case 'drag-rank':
      return local.kind === 'drag-rank' && local.value.length > 0;
    case 'slider':
      return local.kind === 'slider' && Number.isFinite(local.value);
    case 'image-grid':
      return local.kind === 'image-grid' && isMultiPickValid(slot, local.value);
  }
}

/** Translate the card's local value into the engine's
 *  `SlotAnswerValue` for submission. Free-text with voice capability
 *  + audioPath flips into a `FreeTextWithVoice` shape per TDD §4.1. */
export function buildAnswerPayload(
  slot: SlotDefinition,
  local: SlotCardLocalValue,
  audioPath: string | undefined,
): SlotAnswerValue {
  switch (local.kind) {
    case 'single-select':
      return local.value ?? '';
    case 'free-text': {
      const reconstructed = reconstructFreeTextValue(
        local.value.trim(),
        audioPath,
        slot.voiceCapability !== undefined,
      );
      return reconstructed;
    }
    case 'multi-pick':
    case 'drag-rank':
    case 'image-grid':
      return [...local.value];
    case 'slider':
      return local.value;
  }
}

/** Local-state union — one variant per slot type. Carrying the
 *  discriminant in the state avoids switch-on-slot-type at every
 *  read. */
export type SlotCardLocalValue =
  | { kind: 'single-select'; value: string | null }
  | { kind: 'free-text'; value: string }
  | { kind: 'multi-pick'; value: string[] }
  | { kind: 'drag-rank'; value: string[] }
  | { kind: 'slider'; value: number }
  | { kind: 'image-grid'; value: string[] };

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SlotCard({
  gameId,
  slot,
  context,
  audioPath,
  onSubmit,
  onSkip,
  newUi = false,
}: SlotCardProps): React.JSX.Element {
  const [local, setLocal] = useState<SlotCardLocalValue>(() =>
    initialValueForSlot(slot, context.prefilled),
  );
  const [touched, setTouched] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submittable = useMemo(
    () => isSlotAnswerSubmittable(slot, local),
    [slot, local],
  );

  const prefilledUntouched = !touched && context.prefilled !== undefined;

  const handleChange = (next: SlotCardLocalValue): void => {
    setLocal(next);
    if (!touched) setTouched(true);
  };

  const handleSubmit = (): void => {
    if (!submittable || submitted) return;
    setSubmitted(true);
    const value = buildAnswerPayload(slot, local, audioPath);
    onSubmit({ kind: 'answered', value });
  };

  // Codex Round-1 fix on PR #302: detect slots whose configuration
  // makes them un-answerable (e.g. an image-grid with zero images
  // shipped). Without this, a required image-grid would show a "No
  // images configured" body, no Skip button (because required), and
  // a disabled Submit (because not submittable) — the user is stuck.
  // Treat un-answerable required slots as skippable; the engine
  // records 'user-skipped' which is the right semantics for "the
  // template author didn't ship the choices."
  const cannotAnswer = isSlotUnanswerable(slot);
  const showSkip = !slot.required || cannotAnswer;

  const handleSkip = (): void => {
    if (!showSkip || submitted) return;
    setSubmitted(true);
    onSkip({ kind: 'skipped', reason: 'user-skipped' });
  };

  if (!newUi) {
    return (
      <div
        className="rounded-lg border border-crucible-accent/30 bg-crucible-accent/5 px-3 py-3"
        data-testid={`slot-card-${slot.id}`}
        data-game-id={gameId}
        data-slot-type={slot.type}
        data-required={slot.required ? 'true' : 'false'}
      >
        <div className="mb-1.5 flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wide text-white/30">
            Question {context.index} of {context.total}
          </span>
          <span
            className="text-[10px] uppercase tracking-wide text-white/40"
            data-testid={`slot-card-${slot.id}-agent`}
          >
            {context.agentName}
          </span>
          {slot.required && (
            <span
              className="h-1.5 w-1.5 rounded-full bg-amber-400"
              aria-label="required"
              data-testid={`slot-card-${slot.id}-required-dot`}
            />
          )}
          {context.prefilled !== undefined && (
            <span
              className="text-[10px] uppercase tracking-wide text-amber-200/60"
              data-testid={`slot-card-${slot.id}-prefilled-badge`}
            >
              Pre-filled
            </span>
          )}
        </div>

        <p className="mb-3 text-sm font-medium text-white/90">{slot.text}</p>

        <SlotBody
          slot={slot}
          local={local}
          onChange={handleChange}
          audioPath={audioPath}
          prefilledUntouched={prefilledUntouched}
          newUi={newUi}
        />

        <div className="mt-3 flex items-center justify-end gap-2 border-t border-white/10 pt-3">
          {showSkip && (
            <button
              type="button"
              onClick={handleSkip}
              disabled={submitted}
              data-testid={`slot-card-${slot.id}-skip`}
              data-skip-because-unanswerable={cannotAnswer ? 'true' : 'false'}
              className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-white/70 transition-colors hover:border-white/20 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              Skip
            </button>
          )}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!submittable || submitted}
            data-testid={`slot-card-${slot.id}-submit`}
            data-armed={submittable && !submitted ? 'true' : 'false'}
            className="rounded-lg bg-crucible-accent px-4 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Submit
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl border border-white/[0.08] px-5 py-5"
      style={{ background: 'var(--bg-elev-1)', boxShadow: 'var(--shadow-card)' }}
      data-testid={`slot-card-${slot.id}`}
      data-game-id={gameId}
      data-slot-type={slot.type}
      data-required={slot.required ? 'true' : 'false'}
    >
      {/* Header: counter left, agent + badges + optional dismiss right */}
      <div className="mb-3 flex items-center justify-between">
        <span className="text-[12px] tabular-nums" style={{ color: 'var(--text-3)' }}>
          Question {context.index} of {context.total}
        </span>
        <div className="flex items-center gap-2">
          {context.prefilled !== undefined && (
            <span
              className="flex items-center gap-1 text-[11px]"
              style={{ color: 'rgba(251,191,36,0.7)' }}
              data-testid={`slot-card-${slot.id}-prefilled-badge`}
            >
              <Check size={10} strokeWidth={2} />
              Pre-filled
            </span>
          )}
          <span
            className="text-[11px]"
            style={{ color: 'var(--text-3)' }}
            data-testid={`slot-card-${slot.id}-agent`}
          >
            {context.agentName}
          </span>
          {slot.required && (
            <span
              className="h-1.5 w-1.5 rounded-full bg-amber-400"
              aria-label="required"
              data-testid={`slot-card-${slot.id}-required-dot`}
            />
          )}
          {/* × dismiss for optional slots */}
          {showSkip && !cannotAnswer && (
            <button
              type="button"
              onClick={handleSkip}
              disabled={submitted}
              data-testid={`slot-card-${slot.id}-skip`}
              data-skip-because-unanswerable="false"
              className="ml-1 flex h-5 w-5 items-center justify-center rounded-full transition-colors hover:bg-white/[0.08] disabled:opacity-40"
              style={{ color: 'var(--text-3)' }}
              aria-label="Skip question"
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M1 1l8 8M9 1L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
          )}
          {/* Skip for unanswerable slots (no × since it's the only option) */}
          {cannotAnswer && (
            <button
              type="button"
              onClick={handleSkip}
              disabled={submitted}
              data-testid={`slot-card-${slot.id}-skip`}
              data-skip-because-unanswerable="true"
              className="text-[11px] transition-colors disabled:opacity-40"
              style={{ color: 'var(--text-3)' }}
            >
              Skip
            </button>
          )}
        </div>
      </div>

      {/* Question */}
      <p className="mb-4 text-[16px] leading-snug" style={{ color: 'var(--text-1)', fontWeight: 400 }}>
        {slot.text}
      </p>

      <SlotBody
        slot={slot}
        local={local}
        onChange={handleChange}
        audioPath={audioPath}
        prefilledUntouched={prefilledUntouched}
        newUi={newUi}
      />

      {/* Footer */}
      <div className="mt-4 flex items-center justify-end pt-3" style={{ borderTop: '1px solid var(--border-alpha-1)' }}>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!submittable || submitted}
          data-testid={`slot-card-${slot.id}-submit`}
          data-armed={submittable && !submitted ? 'true' : 'false'}
          className="flex items-center gap-1.5 rounded-full bg-crucible-accent px-5 py-1.5 text-[13px] font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          Next
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 6h8M6 2l4 4-4 4"/>
          </svg>
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-type body dispatch
// ---------------------------------------------------------------------------

interface SlotBodyProps {
  slot: SlotDefinition;
  local: SlotCardLocalValue;
  onChange: (next: SlotCardLocalValue) => void;
  audioPath: string | undefined;
  prefilledUntouched: boolean;
  newUi?: boolean;
}

function SlotBody({
  slot,
  local,
  onChange,
  audioPath,
  prefilledUntouched,
  newUi = false,
}: SlotBodyProps): React.JSX.Element {
  switch (slot.type) {
    case 'single-select':
      return (
        <SlotCardSingleSelect
          slot={slot}
          value={local.kind === 'single-select' ? local.value : null}
          onChange={(v) => onChange({ kind: 'single-select', value: v })}
          prefilledUntouched={prefilledUntouched}
          newUi={newUi}
        />
      );
    case 'free-text':
      return (
        <SlotCardFreeText
          slot={slot}
          value={local.kind === 'free-text' ? local.value : ''}
          onChange={(v) => onChange({ kind: 'free-text', value: v })}
          audioPath={audioPath}
          prefilledUntouched={prefilledUntouched}
        />
      );
    case 'multi-pick':
      return (
        <SlotCardMultiPick
          slot={slot}
          value={local.kind === 'multi-pick' ? local.value : []}
          onChange={(v) => onChange({ kind: 'multi-pick', value: v })}
          prefilledUntouched={prefilledUntouched}
        />
      );
    case 'drag-rank':
      return (
        <SlotCardDragRank
          slot={slot}
          value={local.kind === 'drag-rank' ? local.value : defaultRankFromOptions(slot)}
          onChange={(v) => onChange({ kind: 'drag-rank', value: v })}
          prefilledUntouched={prefilledUntouched}
        />
      );
    case 'slider':
      return (
        <SlotCardSlider
          slot={slot}
          value={local.kind === 'slider' ? local.value : defaultSliderValue(slot)}
          onChange={(v) => onChange({ kind: 'slider', value: v })}
          prefilledUntouched={prefilledUntouched}
        />
      );
    case 'image-grid':
      return (
        <SlotCardImageGrid
          slot={slot}
          value={local.kind === 'image-grid' ? local.value : []}
          onChange={(v) => onChange({ kind: 'image-grid', value: v })}
          prefilledUntouched={prefilledUntouched}
        />
      );
  }
}

// ---------------------------------------------------------------------------
// Re-exports for tests / external callers
// ---------------------------------------------------------------------------

export type { FreeTextWithVoice };
